/**
 * §6 ACCESSIBILITY — the settings themselves, as pure data and pure functions.
 *
 * NO DOM, NO STORAGE, NO NETWORK, NO IMPORTS. Everything in this file is a
 * value or a total function over values, which is what lets the store, the DOM
 * applier, the match HUD and the tests all agree on one definition of "text
 * scale 1.3" without any of them being able to reinterpret it.
 *
 * ── THE FOUR SETTINGS, AND WHY EACH IS THE SHAPE IT IS ──────────────────────
 *
 * `teamPalette`   STYLE_BIBLE §3's colour-blind pairs. This is NOT a new
 *                 palette: `engine/match/palette.ts` already ships the three
 *                 pairs and `setTeamPalette()` already applies them. This
 *                 setting is the SWITCH that was never wired to anything a
 *                 player could reach outside the training menu.
 *                 Team identity is never colour alone — every ownership
 *                 surface also carries `teamShape()` — so a wrong choice here
 *                 costs legibility, never information.
 *
 * `motion`        THREE states, not a boolean: `system` (follow
 *                 `prefers-reduced-motion`), `full` and `reduced`. A boolean
 *                 cannot express "I set my OS to reduce motion but I want the
 *                 game's animation", and it cannot express the opposite
 *                 either — which is the common case, because a player on a
 *                 borrowed or corporate machine cannot change the OS setting.
 *                 §6 asks for "the media query PLUS an explicit toggle", and
 *                 an explicit toggle that silently loses to the media query is
 *                 not a toggle. `resolveReducedMotion()` is the only place the
 *                 two are combined.
 *
 * `textScale`     A MULTIPLIER ON THE ROOT FONT SIZE, deliberately, not a
 *                 font-size override on body text. Every tap target in this
 *                 codebase is expressed in `rem` (`min-h-11` = 2.75rem = 44px
 *                 at the 16px default), so scaling the root scales the TARGETS
 *                 WITH THE TYPE. That is the entire reason a 44px floor
 *                 survives a text-scale setting: at every step the effective
 *                 target is `44 * scale`, which is monotonically >= 44 because
 *                 `TEXT_SCALE_MIN` is 1. Scaling only the glyphs would grow the
 *                 label inside a box that did not grow, which overflows at
 *                 320px — the failure mode this shape exists to avoid.
 *
 *                 THE MINIMUM IS 1, NOT 0.8. The match HUD's own
 *                 `TEXT_SCALE_MIN` is 0.8 because a canvas HUD's glyphs are
 *                 drawn independently of its hit boxes and shrinking them is
 *                 safe there. In the DOM they are the same box, so a scale
 *                 below 1 would push a 44px target to 35px. An accessibility
 *                 setting must not be able to make the product less accessible.
 *
 * `mirrored`      §6's left-handed HUD mirror. Phase 2 built the HUD layout as
 *                 "place right-handed, then reflect", so this is a transform
 *                 (`computeHudLayout`'s single `mirrorRect` loop), not a second
 *                 code path — and a true reflection is a property the tests can
 *                 check arithmetically rather than by eye.
 *
 * `announcements` Whether the live regions speak match state (mana full, tower
 *                 destroyed, match result). On by default. It is a setting
 *                 because a screen-reader user in a fast match can legitimately
 *                 want the board quiet, and because a live region nobody can
 *                 silence is a worse experience than none.
 *
 * ── WHAT IS NOT HERE ────────────────────────────────────────────────────────
 * No colours. `engine/match/palette.ts` owns the hexes and the §4 contrast
 * proof; duplicating them here would create a second definition of a law that
 * only holds if there is one. This file names a palette; it never describes it.
 */

/* ═════════════════════════════════════════════════════════════ the union ══ */

/**
 * STYLE_BIBLE §3's three team-colour pairs, by id.
 *
 * The same union as `engine/meta/menu.ts#TeamPaletteChoice` and
 * `engine/match/palette.ts#TeamPaletteId`. It is spelled out a third time here
 * rather than imported for the ADR-0005 reason `menu.ts` already documents:
 * `palette.ts` lives under `engine/match/**` and importing it from a module the
 * SHELL loads would drag the match chunk (Pixi included) into first paint.
 *
 * The drift is bounded in both directions: `setTeamPalette()` validates against
 * the palette module's own table and falls back to `default`, and
 * `TEAM_PALETTE_CHOICES` below is asserted against that table in
 * `test/a11y.test.ts`. A rename upstream fails a test here; it never reaches a
 * player as a crash.
 */
export type TeamPaletteChoice = 'default' | 'deuteranopia' | 'tritanopia';

export const TEAM_PALETTE_CHOICES: readonly TeamPaletteChoice[] = Object.freeze([
  'default',
  'deuteranopia',
  'tritanopia',
]);

/** `system` = follow `prefers-reduced-motion`. See the header. */
export type MotionPreference = 'system' | 'full' | 'reduced';

export const MOTION_PREFERENCES: readonly MotionPreference[] = Object.freeze([
  'system',
  'full',
  'reduced',
]);

/* ══════════════════════════════════════════════════════════ text scale ══ */

/**
 * The reference root font size, in CSS px. Browsers default to 16 and every
 * `rem` in this codebase is authored against it.
 */
export const BASE_FONT_PX = 16;

/** WCAG 2.2 target size (minimum). Every interactive box clears this. */
export const MIN_TAP_TARGET_PX = 44;

/**
 * 1 is the floor, on purpose — see the header. 1.5 is the ceiling because at
 * 320px viewport width a 1.5x root makes the tab bar's five labels the widest
 * row in the product; past that they stop fitting on one line and the bar grows
 * a second one, which moves the thumb reach §6 is built around.
 */
export const TEXT_SCALE_MIN = 1;
export const TEXT_SCALE_MAX = 1.5;

/**
 * The offered steps. A slider would be finer, but a screen-reader user tabbing
 * a slider hears "1.05, 1.1, 1.15…" and cannot tell which one they wanted; four
 * named steps are a radio group with four sayable names.
 */
export const TEXT_SCALE_STEPS: readonly number[] = Object.freeze([1, 1.15, 1.3, 1.5]);

/** i18n key suffixes for the four steps, index-matched to `TEXT_SCALE_STEPS`. */
export const TEXT_SCALE_STEP_KEYS: readonly string[] = Object.freeze([
  'a11y.text_scale.normal',
  'a11y.text_scale.large',
  'a11y.text_scale.larger',
  'a11y.text_scale.largest',
]);

/** Clamp into [TEXT_SCALE_MIN, TEXT_SCALE_MAX]. Non-finite input -> 1. */
export function clampTextScale(value: number): number {
  if (!Number.isFinite(value)) return 1;
  if (value < TEXT_SCALE_MIN) return TEXT_SCALE_MIN;
  if (value > TEXT_SCALE_MAX) return TEXT_SCALE_MAX;
  // Two decimals: the value goes into a CSS string and into a storage key, and
  // 1.1500000000000001 in either is noise that breaks a string compare.
  return Math.round(value * 100) / 100;
}

/** The step at or below `value` — what a radio group should show as chosen. */
export function nearestTextScaleStep(value: number): number {
  const v = clampTextScale(value);
  let best = TEXT_SCALE_STEPS[0] as number;
  let bestDelta = Math.abs(v - best);
  for (const step of TEXT_SCALE_STEPS) {
    const delta = Math.abs(v - step);
    if (delta < bestDelta) {
      best = step;
      bestDelta = delta;
    }
  }
  return best;
}

/** Root font size, CSS px, for a scale. What `apply.ts` writes. */
export function rootFontPx(scale: number): number {
  return Math.round(BASE_FONT_PX * clampTextScale(scale) * 100) / 100;
}

/**
 * The effective size, in CSS px, of a target authored as `min-h-11` (2.75rem)
 * at this text scale.
 *
 * THIS IS THE 44px PROOF, and it is arithmetic rather than a measurement
 * because jsdom has no layout engine (see `CLAUDE.md` and the note at the top
 * of `test/meta-screens.test.ts`). A rem-authored target scales with the root,
 * `clampTextScale` cannot return less than 1, so the product cannot drop below
 * 44. A future px-authored target would not be covered by this — which is why
 * `test/a11y.test.ts` also greps the meta screens for px-valued min-heights.
 */
export function tapTargetPx(scale: number): number {
  return (MIN_TAP_TARGET_PX / BASE_FONT_PX) * rootFontPx(scale);
}

/* ═══════════════════════════════════════════════════════════ the record ══ */

export interface A11ySettings {
  readonly teamPalette: TeamPaletteChoice;
  readonly motion: MotionPreference;
  /** 1 .. 1.5, two decimals. */
  readonly textScale: number;
  /** §6 left-handed HUD mirror. */
  readonly mirrored: boolean;
  /** Speak match state through the live regions. */
  readonly announcements: boolean;
}

export const DEFAULT_A11Y_SETTINGS: Readonly<A11ySettings> = Object.freeze({
  teamPalette: 'default',
  motion: 'system',
  textScale: 1,
  mirrored: false,
  announcements: true,
});

export function isTeamPaletteChoice(v: unknown): v is TeamPaletteChoice {
  return typeof v === 'string' && (TEAM_PALETTE_CHOICES as readonly string[]).includes(v);
}

export function isMotionPreference(v: unknown): v is MotionPreference {
  return typeof v === 'string' && (MOTION_PREFERENCES as readonly string[]).includes(v);
}

/**
 * Coerce anything into a valid settings record. TOTAL: it never throws and
 * never returns a field outside its domain, because its inputs are a
 * `localStorage` string a player can edit and a JSON body a server can change.
 */
export function normaliseA11ySettings(input: unknown): A11ySettings {
  const o = (typeof input === 'object' && input !== null ? input : {}) as Record<string, unknown>;
  const scale = typeof o['textScale'] === 'number'
    ? o['textScale']
    : Number.parseFloat(String(o['textScale'] ?? ''));
  return Object.freeze({
    teamPalette: isTeamPaletteChoice(o['teamPalette'])
      ? o['teamPalette']
      : DEFAULT_A11Y_SETTINGS.teamPalette,
    motion: isMotionPreference(o['motion']) ? o['motion'] : DEFAULT_A11Y_SETTINGS.motion,
    textScale: Number.isFinite(scale) ? clampTextScale(scale) : DEFAULT_A11Y_SETTINGS.textScale,
    mirrored: o['mirrored'] === true || o['mirrored'] === '1' || o['mirrored'] === 1,
    // Absent means DEFAULT (on), not off: a stored record written before this
    // field existed must not silence a player's match.
    announcements: o['announcements'] === undefined
      ? DEFAULT_A11Y_SETTINGS.announcements
      : !(o['announcements'] === false || o['announcements'] === '0' || o['announcements'] === 0),
  });
}

/** Field-by-field equality. Used to skip a write and a re-render. */
export function a11ySettingsEqual(a: A11ySettings, b: A11ySettings): boolean {
  return (
    a.teamPalette === b.teamPalette &&
    a.motion === b.motion &&
    a.textScale === b.textScale &&
    a.mirrored === b.mirrored &&
    a.announcements === b.announcements
  );
}

/* ═════════════════════════════════════════════════════════════ motion ══ */

/**
 * THE ONLY PLACE the explicit setting and the media query are combined.
 *
 *   `reduced` -> true    regardless of the OS
 *   `full`    -> false   regardless of the OS
 *   `system`  -> the media query
 *
 * `systemPrefersReduced` is passed in rather than read, so this stays pure and
 * so a test can assert both branches without a fake `matchMedia`.
 */
export function resolveReducedMotion(
  settings: Pick<A11ySettings, 'motion'>,
  systemPrefersReduced: boolean,
): boolean {
  if (settings.motion === 'reduced') return true;
  if (settings.motion === 'full') return false;
  return systemPrefersReduced;
}

/* ══════════════════════════════════════════════════════ the DOM contract ══ */

/**
 * The attributes `apply.ts` writes onto `<html>`, and the CSS in `apply.ts`
 * selects on. Named here so the applier, the stylesheet and the tests share one
 * spelling — a typo'd `data-` attribute is silent in every one of them.
 */
export const A11Y_ATTR = Object.freeze({
  motion: 'data-rga-motion',
  textScale: 'data-rga-text-scale',
  palette: 'data-rga-palette',
  mirrored: 'data-rga-mirrored',
});

/** CSS custom property carrying the scale, for anything sized off it. */
export const A11Y_SCALE_VAR = '--rga-text-scale';

export interface A11yDomState {
  /** `'reduced'` or `'full'` — RESOLVED, never `'system'`: CSS cannot resolve. */
  readonly motion: 'reduced' | 'full';
  readonly textScale: string;
  readonly palette: TeamPaletteChoice;
  readonly mirrored: 'on' | 'off';
  readonly rootFontPx: number;
}

export function a11yDomState(
  settings: A11ySettings,
  systemPrefersReduced: boolean,
): A11yDomState {
  return Object.freeze({
    motion: resolveReducedMotion(settings, systemPrefersReduced) ? 'reduced' : 'full',
    textScale: String(clampTextScale(settings.textScale)),
    palette: settings.teamPalette,
    mirrored: settings.mirrored ? 'on' : 'off',
    rootFontPx: rootFontPx(settings.textScale),
  } as const);
}

/* ═══════════════════════════════════════════════════════════ storage keys ══ */

/**
 * DEVICE-LOCAL KEYS, and two of them are DELIBERATELY THE OLD ONES.
 *
 * `engine/meta/menu.ts` has persisted `rga.hud.mirrored` and
 * `rga.hud.teamPalette` since Phase 2, and `engine/bootstrap.ts` still reads
 * them through `readMenuSettings()`. Writing the same two keys — rather than a
 * fresh namespace plus a migration — means there is exactly ONE stored answer
 * to "is the HUD mirrored", so the training menu's own toggle and this settings
 * screen cannot disagree, and a player who set the mirror before this phase
 * keeps it.
 */
export const A11Y_STORAGE_KEYS = Object.freeze({
  /** Shared with `engine/meta/menu.ts`. Do not rename without renaming both. */
  mirrored: 'rga.hud.mirrored',
  /** Shared with `engine/meta/menu.ts`. */
  teamPalette: 'rga.hud.teamPalette',
  motion: 'rga.a11y.motion',
  textScale: 'rga.a11y.textScale',
  announcements: 'rga.a11y.announcements',
});
